/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import { matchService } from '../../services/matchService';
import { messageService } from '../../services/messageService';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import { useTranslation } from 'react-i18next';
import './Messages.css';

const MATCHES_PER_PAGE = 10;

const Messages = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMoreMatches, setHasMoreMatches] = useState(true);
  const [lastConversationDoc, setLastConversationDoc] = useState(null);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const authState = useSelector(state => state.auth);
  const { t } = useTranslation();

  const fetchUserDetails = async (uid) => {
    try {
      const data = await matchService.getUserDetails(uid);
      return {
        name: data.name,
        avatar: data.photos?.[0]?.photoUrl
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      return { name: t('messages.defaultName'), avatar: null };
    }
  };

  const fetchRecentMatches = async (offset = 0) => {
    try {
      setLoadingMore(true);
      const response = await matchService.getUserMatches(
        authState.uid, 
        offset,
        MATCHES_PER_PAGE
      );

      if (response && response.matches && response.matches.length > 0) {
        const matchesWithDetails = await Promise.all(
          response.matches.map(async (match) => {
            const otherUserId = match.members.find(id => id !== authState.uid);
            const userDetails = await fetchUserDetails(otherUserId);
            return {
              ...match,
              otherUser: {
                uid: otherUserId,
                ...userDetails
              }
            };
          })
        );

        setRecentMatches(prev => offset === 0 ? matchesWithDetails : [...prev, ...matchesWithDetails]);
        setCurrentOffset(response.nextOffset);
        setHasMoreMatches(response.hasMore);
      } else {
        setHasMoreMatches(false);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchRecentMatches(0);
  // Ne doit se déclencher que sur le changement d'utilisateur, pas sur fetchRecentMatches
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.uid]);

  useEffect(() => {
    setLoading(true);

    // Subscribe to conversations
    // Note : searchQuery n'est PAS une dépendance ici : le filtrage par recherche
    // est déjà géré côté client par le useEffect [searchQuery, conversations] plus bas.
    // Le mettre en dépendance recréait tout l'abonnement Firestore à chaque frappe clavier
    // (spinner plein écran + re-téléchargement de tous les utilisateurs à chaque caractère tapé).
    const unsubscribe = messageService.subscribeToConversations(
      authState.uid,
      ({ changes, lastDoc, hasMore, allConversations }) => {
        setLoading(false);
        setLastConversationDoc(lastDoc);
        setHasMoreConversations(hasMore);
        setConversations(allConversations);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [authState.uid]);

  const loadMoreConversations = async () => {
    if (!hasMoreConversations || loadingMoreConversations) return;
    setLoadingMoreConversations(true);

    try {
      const result = await messageService.loadMoreConversations(authState.uid, lastConversationDoc);
      const newConversationsWithUsers = await Promise.all(
        result.conversations.map(async (conversation) => {
          const otherUserId = conversation.members.find(id => id !== authState.uid);
          const userDetails = await fetchUserDetails(otherUserId);
          
          return {
            id: conversation.id,
            ...conversation,
            otherUser: {
              uid: otherUserId,
              ...userDetails
            }
          };
        })
      );

      const newConversationsWithMessages = newConversationsWithUsers.filter(conv => conv.latestMessage !== null);
      setConversations(prev => [...prev, ...newConversationsWithMessages]);
      setFilteredConversations(prev => [...prev, ...newConversationsWithMessages]);
      setLastConversationDoc(result.lastDoc);
      setHasMoreConversations(result.hasMore);
    } catch (error) {
      console.error('Error loading more conversations:', error);
    } finally {
      setLoadingMoreConversations(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
      return;
    }

    const filtered = conversations.filter(conversation => 
      conversation.otherUser.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredConversations(filtered);
  }, [searchQuery, conversations]);

  const handleChatClick = async (conversationId) => {
    try {
      // Mark the conversation as read before navigating

      navigate(`/chat/${conversationId}`);
    } catch (error) {
      console.error('Error handling chat click:', error);
      navigate(`/chat/${conversationId}`);
    }
  };

  if (loading) {
    return <Spin size="large" fullscreen tip={t('messages.loading')} />;
  }

  return (
    <div className="page-wrapper has-menubar">
      {/* Header */}
      <header className="header header-fixed border-0 style-2">
        <div className="container">
          <div className="header-content">
            <div className="left-content header-logo logo-lg">
              <Link to="/">
                <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="Logo" />
              </Link>
            </div>
            <div className="mid-content">
            </div>
            <div className="right-content">
              <button onClick={() => {}} className="font-22 btn-link">
                <i className="flaticon flaticon-computer-security-shield"></i>
              </button>	
            </div>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="content-body p-t50 p-b50">
        <div className="container">
          <div className="dz-chat-search input-group input-group-icon input-mini">
            <div className="input-group-text">
              <div className="input-icon">
                <i className="icon feather icon-search"></i>
              </div>
            </div>
            <input 
              type="text" 
              className="form-control" 
              placeholder={t('messages.searchPlaceholder')} 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />								
          </div>
          
          {recentMatches.length > 0 && (
            <>
              <h6 className="tiltle mb-3">{t('messages.newMatches')}</h6>
              <Swiper
                modules={[Navigation, FreeMode]}
                slidesPerView="auto"
                spaceBetween={15}
                freeMode={true}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
                onReachEnd={() => {
                  if (hasMoreMatches && !loadingMore) {
                    fetchRecentMatches(currentOffset);
                  }
                }}
                className="chat-swiper"
              >
                {recentMatches.map((match) => (
                  <SwiperSlide key={match.otherUser.uid}>
                    <div 
                      className={`recent ${match.active ? 'active' : ''}`}
                      onClick={() => navigate(`/chat/${match.customId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="media media-60 rounded-circle">
                        <img src={match.otherUser.avatar || require('../../assets/images/user/pic1.jpg')} alt={match.otherUser.name} />
                      </div>
                      <span>{match.otherUser.name}</span>
                    </div>
                  </SwiperSlide>
                ))}
                <div className="swiper-button-next"></div>
                <div className="swiper-button-prev"></div>
                {loadingMore && (
                  <div className="swiper-loading">
                    <Spin size="small" />
                  </div>
                )}
              </Swiper>
            </>
          )}

          <div className="title-bar">
            <h6 className="title">{t('messages.title')}</h6>
          </div>

          <div className="message-list-container">
            <ul className="message-list">
              {filteredConversations.map((conversation) => (
                <li key={conversation.id} className={`${conversation.active ? 'active' : ''} ${conversation.isUnread ? 'unread' : ''}`}>
                  <a onClick={() => handleChatClick(conversation.id)} style={{ cursor: 'pointer' }}>
                    <div className="media media-60">
                      <img 
                        src={conversation.otherUser.avatar || require('../../assets/images/user/pic1.jpg')} 
                        alt={conversation.otherUser.name} 
                      />
                    </div>
                    <div className="media-content">
                      <div>
                        <h6 className="name">{conversation.otherUser.name}</h6>
                        <p className="last-msg">
                          {conversation.latestMessage?.text || t('messages.noMessage')}
                          {conversation.isUnread && <span className="unread-indicator">•</span>}
                        </p>
                      </div>
                      <div className="right-content">
                        <span className="date">{conversation.latestMessage?.createdAt ? 
                          new Date(conversation.latestMessage.createdAt * 1000).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  </a>
                </li>
              ))}
              
              {filteredConversations.length === 0 && searchQuery && (
                <div className="no-results">
                  <p>{t('messages.noResults', { query: searchQuery })}</p>
                </div>
              )}
            </ul>

            {hasMoreConversations && (
              <div className="load-more-container">
                <button 
                  className="btn btn-primary w-100" 
                  onClick={loadMoreConversations}
                  disabled={loadingMoreConversations}
                >
                  {loadingMoreConversations ? (
                    <Spin size="small" />
                  ) : (
                    t('messages.loadMore')
                  )}
                </button>
              </div>
            )}
          </div>
        </div>    
      </div>

      {/* Menubar */}

    </div>
  );
};

export default Messages;
